<template>
  <section class="flex flex-col min-h-0 h-full rounded-[12px] bg-[#161c22] border border-[#232a31] p-4" data-testid="summary-panel">
    <header class="flex items-center gap-2 mb-3">
      <img src="@/assets/icons/sorenson/sparkle.svg" class="w-[18px] h-[18px]" alt="">
      <h2 class="text-[15px] font-semibold text-white">{{ translate('ai_summary_title', 'AI summary') }}</h2>
    </header>

    <div class="flex-1 min-h-0 overflow-y-auto scrollbar-thin scrollbar-thumb-[#f0f6ff57] scrollbar-track-transparent -mx-1 px-1">

      <!-- Nothing selected -->
      <template v-if="!transcript">
        <div class="rounded-[8px] bg-[#1e2428] p-3 text-[13px]" data-testid="summary-no-selection">
          <div class="font-semibold text-white">{{ translate('ai_summary_no_selection_title', 'No transcription has been selected') }}</div>
          <div class="text-white/75 mt-1">{{ translate('ai_summary_no_selection_text', 'Select a transcription, then generate a summary.') }}</div>
        </div>
        <button type="button" disabled class="mt-3 w-full h-[38px] rounded-[8px] bg-white/5 text-white/30 text-[14px] flex items-center justify-center gap-2 cursor-not-allowed">
          <img src="@/assets/icons/sorenson/sparkle.svg" class="w-4 h-4 opacity-30 grayscale" alt="">
          {{ translate('ai_summary_generate', 'Generate summary') }}
        </button>
        <div class="h-px bg-[#232a31] my-4"></div>
        <summary-includes-list :sections="sections" />
        <p class="text-[11px] text-white/55 mt-4">{{ disclaimer }}</p>
      </template>

      <!-- Checking whether a summary already exists -->
      <div v-else-if="!state.loaded && state.status === 'idle'" class="flex flex-col gap-3">
        <div class="h-[120px] rounded-[10px] bg-[#0c1116] animate-pulse"></div>
        <div class="h-[70px] rounded-[8px] bg-white/5 animate-pulse"></div>
      </div>

      <!-- Generating -->
      <template v-else-if="state.status === 'generating'">
        <div class="rounded-[10px] bg-[#0c1116] p-4" data-testid="summary-generating">
          <h3 class="text-[17px] font-semibold text-white">{{ translate('ai_summary_building_title', 'Building your session summary') }}</h3>
          <p class="text-[13px] text-white/75 mt-1">{{ translate('ai_summary_building_text', 'We\'re turning this session into clear, source-linked event notes.') }}</p>
          <ul class="flex flex-col gap-3 mt-4 text-[13px]">
            <li v-for="(step, index) in steps" :key="step.key" class="flex items-center gap-2"
                :class="{ 'text-white': stepStatus(index) === 'done', 'text-primary': stepStatus(index) === 'active', 'text-white/55': stepStatus(index) === 'pending' }">
              <lib-icon v-if="stepStatus(index) === 'done'" name="general/check-circle/outline" size="16" color="#3ccf9a" />
              <span v-else-if="stepStatus(index) === 'active'" class="w-4 h-4 rounded-full border-2 border-primary border-t-transparent animate-spin"></span>
              <span v-else class="w-4 h-4"></span>
              {{ step.label }}
            </li>
          </ul>
          <p class="text-[11px] text-white/55 mt-4">{{ translate('ai_summary_building_hint', 'Usually takes less than a minute. You can keep reading the transcript.') }}</p>
          <button type="button" class="mt-3 w-full h-[38px] rounded-[8px] bg-[#1e2428] hover:bg-[#283038] text-white text-[14px] cursor-pointer"
                  data-testid="summary-cancel" @click="$emit('cancel')">
            {{ $t('common_cancel') }}
          </button>
        </div>
        <div class="h-px bg-[#232a31] my-4"></div>
        <div v-for="section in sections" :key="section.key" class="mb-5">
          <div class="text-[12px] font-semibold text-white/55 mb-2">{{ section.title }}</div>
          <div class="h-2 rounded bg-white/5 animate-pulse mb-2"></div>
          <div class="h-2 rounded bg-white/5 animate-pulse mb-2"></div>
          <div class="h-2 w-2/3 rounded bg-white/5 animate-pulse"></div>
        </div>
        <p class="text-[11px] text-white/55">{{ disclaimer }}</p>
      </template>

      <!-- Generated -->
      <template v-else-if="state.status === 'ready' && state.summary">
        <selected-session-card :transcript="transcript" />
        <div class="flex items-center gap-1.5 text-[11px] text-[#3ccf9a] mt-3">
          <lib-icon name="general/check-circle/outline" size="14" />
          {{ translate('ai_summary_generated', 'Summary generated') }} · {{ transcript.title }} {{ translate('ai_summary_only', 'only') }}
        </div>
        <button type="button" class="mt-2 w-full h-[38px] rounded-[8px] bg-primary hover:opacity-90 text-[#0c1116] text-[14px] font-medium flex items-center justify-center gap-2 cursor-pointer"
                data-testid="summary-copy" @click="copySummary">
          <lib-icon name="files/clipboard/outline" size="16" />
          {{ translate('ai_summary_copy', 'Copy summary') }}
        </button>

        <div v-for="section in sections" :key="section.key" class="border-t border-[#232a31] mt-4 pt-4" :data-testid="`summary-section-${section.key}`">
          <div class="flex items-center gap-2 mb-3">
            <span class="w-7 h-7 rounded-[6px] bg-[#1e2428] flex items-center justify-center text-primary">
              <lib-icon :name="section.icon" size="16" />
            </span>
            <h3 class="text-[13px] font-semibold text-white">{{ section.title }}</h3>
          </div>
          <div v-for="(item, index) in state.summary[section.key] || []" :key="index" class="mb-4 text-[13px] leading-[19px] text-white/89">
            <p>
              <span v-if="item.title" class="font-semibold text-white">{{ item.title }}</span>
              {{ item.text }}
            </p>
            <button type="button" class="mt-1 text-[11px] text-primary hover:underline cursor-pointer tabular-nums"
                    data-testid="summary-timecode" @click="$emit('jump', { start: item.start, end: item.end })">
              <template v-if="item.label">{{ item.label }} · </template>{{ formatRange(item) }}
            </button>
          </div>
        </div>

        <p class="text-[11px] text-white/55 mt-1">{{ disclaimer }}</p>
        <div class="flex gap-2 mt-3">
          <button v-for="option in feedbackOptions" :key="option.value" type="button"
                  class="h-[30px] px-3 rounded-[8px] border text-[13px] flex items-center gap-1.5 cursor-pointer"
                  :class="state.feedback === option.value ? 'border-primary text-primary bg-primary/10' : 'border-[#3a4149] text-white bg-[#1e2428] hover:bg-[#283038]'"
                  :data-testid="`summary-feedback-${option.value}`"
                  @click="$emit('feedback', option.value)">
            <lib-icon :name="option.icon" size="16" />
            {{ option.label }}
          </button>
        </div>
      </template>

      <!-- Ready to generate / error -->
      <template v-else>
        <div v-if="state.status === 'error'" class="rounded-[8px] bg-[#4a2224] p-3 mb-3 flex gap-2 text-[13px]" data-testid="summary-error">
          <lib-icon name="alerts/alert-circle/outline" size="16" color="#FF6566" class="shrink-0 mt-0.5" />
          <div>
            <div class="font-semibold text-white">{{ translate('ai_summary_error_title', 'We couldn\'t generate your summary') }}</div>
            <div class="text-white/75 mt-0.5">{{ translate('ai_summary_error_text', 'The summary service didn\'t respond. Your session and transcript are unchanged. Try again in a moment.') }}</div>
          </div>
        </div>

        <selected-session-card :transcript="transcript" :title="translate('ai_summary_ready_title', 'Ready to generate summary')">
          <button type="button" class="mt-3 w-full h-[38px] rounded-[8px] bg-primary hover:opacity-90 text-[#0c1116] text-[14px] font-medium flex items-center justify-center gap-2 cursor-pointer"
                  data-testid="summary-generate" @click="$emit('generate')">
            <img src="@/assets/icons/sorenson/sparkle.svg" class="w-4 h-4 brightness-0" alt="">
            {{ state.status === 'error' ? translate('ai_summary_try_again', 'Try again') : translate('ai_summary_generate', 'Generate summary') }}
          </button>
        </selected-session-card>

        <template v-if="state.status === 'error'">
          <p class="text-[13px] text-white/89 mt-4">{{ translate('ai_summary_error_hint', 'You can search, read and verify the transcript while summary generation is unavailable.') }}</p>
          <p v-if="transcriptRange" class="text-[11px] text-white/55 mt-2">
            {{ translate('ai_summary_retry_range', 'Retry uses the same range:') }} {{ transcriptRange }}. {{ translate('ai_summary_retry_scope', 'No other sessions will be included.') }}
          </p>
          <div class="h-px bg-[#232a31] my-4"></div>
          <h3 class="text-[13px] font-semibold text-white">{{ translate('ai_summary_error_persist_title', 'If this keeps happening') }}</h3>
          <p class="text-[13px] text-white/89 mt-2">{{ translate('ai_summary_error_persist_text', 'Check your connection before retrying.') }}</p>
        </template>

        <template v-else>
          <div class="rounded-[8px] bg-[#1e2428] p-3 mt-3 text-[13px]" data-testid="summary-not-generated">
            <div class="font-semibold text-white">{{ translate('ai_summary_none_title', 'No summary has been generated for this session.') }}</div>
            <div class="text-white/75 mt-1">{{ translate('ai_summary_none_text', 'Summaries only start when you click Generate summary. You can read the transcript at any time.') }}</div>
          </div>
          <div class="h-px bg-[#232a31] my-4"></div>
          <summary-includes-list :sections="sections" />
        </template>

        <p class="text-[11px] text-white/55 mt-4">{{ disclaimer }}</p>
      </template>
    </div>
  </section>
</template>

<script setup>
import { computed, defineEmits, defineProps } from 'vue';
import LibIcon from '@/components/ui/LibIcon.vue';
import SelectedSessionCard from '@/components/transcripts/SelectedSessionCard.vue';
import SummaryIncludesList from '@/components/transcripts/SummaryIncludesList.vue';
import { toast } from '@/plugins/toast';
import { translate } from '@/composables/useCaptionSettings';
import { formatTimecode } from '@/utils/transcript-date';

const props = defineProps({
  // Selected transcript (with messages), or null when nothing is selected.
  transcript: { type: Object, default: null },
  // Entry from useTranscriptSummary().summaryState()
  state: { type: Object, default: null },
});

defineEmits(['generate', 'cancel', 'jump', 'feedback']);

const disclaimer = translate('ai_summary_disclaimer', 'AI can miss details. Use the transcript to verify your summary.');

const sections = [
  { key: 'key_takeaways', icon: 'education/book-open/outline', title: translate('ai_summary_key_takeaways', 'Key takeaways'), description: translate('ai_summary_key_takeaways_desc', 'Main ideas from this session.') },
  { key: 'speaker_highlights', icon: 'media/lightbulb/outline', title: translate('ai_summary_speaker_highlights', 'Speaker highlights'), description: translate('ai_summary_speaker_highlights_desc', 'Notable insights from the speaker.') },
  { key: 'follow_up_questions', icon: 'communication/annotation-question/outline', title: translate('ai_summary_follow_up', 'Follow-up questions'), description: translate('ai_summary_follow_up_desc', 'Questions to explore after the session.') },
];

const steps = [
  { key: 'transcript_ready', label: translate('ai_summary_step_transcript', 'Selected transcript ready') },
  { key: 'highlights', label: translate('ai_summary_step_highlights', 'Finding takeaways and highlights') },
  { key: 'questions', label: translate('ai_summary_step_questions', 'Writing follow-up questions') },
];

const feedbackOptions = [
  { value: 'helpful', icon: 'alerts/thumbs-up/outline', label: translate('ai_summary_helpful', 'Helpful') },
  { value: 'not_helpful', icon: 'alerts/thumbs-down/outline', label: translate('ai_summary_not_helpful', 'Not helpful') },
];

function stepStatus(index) {
  const current = steps.findIndex(step => step.key === props.state?.step);
  if (index < current) return 'done';
  return index === Math.max(current, 0) ? 'active' : 'pending';
}

function formatRange({ start, end }) {
  return end ? `${formatTimecode(start)}–${formatTimecode(end)}` : formatTimecode(start);
}

const transcriptRange = computed(() => {
  const messages = props.transcript?.messages || [];
  if (!messages.length) return '';
  return formatRange({ start: messages[0].time, end: messages[messages.length - 1].time });
});

async function copySummary() {
  const { summary } = props.state;
  const text = [
    props.transcript.title,
    ...sections.flatMap(section => [
      '',
      section.title,
      ...(summary[section.key] || []).map(item =>
        `- ${[item.title, item.text].filter(Boolean).join(' ')} (${formatRange(item)})`
      ),
    ]),
  ].join('\n');

  try {
    await navigator.clipboard.writeText(text);
    toast.success(translate('ai_summary_copied_msg', 'Summary copied'));
  } catch (error) {
    console.error(error.message);
  }
}
</script>
